import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AlertMsg from '../../../component/AlertMsg/AlertMsg';
import Loading from "../../../component/Loading/Loading";
import useAxiosInstance from '../../../services/useAxiosInstance';
import { Upload, Calendar, Type, FileText } from "lucide-react";

const CreateNew = () => {
  const axiosInstance = useAxiosInstance();
  const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);
  const clientId = useSelector((state) => state.auth.clientId);
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    communityid: communityId,
    createdbyid: userId,
    createddate: getToday(),
    datepublished: getToday(),
    modifiedbyid: userId,
    modifieddate: getToday(),
    newsUrl: '',
    newscontent: '',
    newsid: 0,
    publishtoclientid: clientId,
    title: '',
  });

  function getToday() {
    return new Date().toISOString().split('T')[0];
  }

  const history = useHistory();

  const handleCancel = () => {
    setFormData({
      ...formData,
      title: '',
      newscontent: '',
      datepublished: getToday(),
    });
    setErrors({});
    history.goBack();
  };

  function validateForm() {
    const newErrors = {};

    if (formData.title.trim() === '') {
      newErrors.title = 'Title is required.';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title is too long. Maximum 100 characters allowed.';
    }

    if (formData.newscontent.trim() === '') {
      newErrors.newscontent = 'News content is required.';
    } else if (formData.newscontent.length > 10000) {
      newErrors.newscontent = 'News content is too long. Maximum 10000 characters allowed.';
    }

    if (!formData.datepublished) {
      newErrors.datepublished = 'Date is required.';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  const handleSubmit = async () => {
    setLoading(true);
    const isValid = validateForm();

    if (isValid) {
      try {
        let newsDocUrl = formData.newsUrl;
        if (selectedFile) {
          const formDataFile = new FormData();
          formDataFile.append('file', selectedFile);

          const uploadResponse = await axiosInstance.post('/api/public/news/document/upload', formDataFile, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          console.log('Upload response:', uploadResponse.data);

          newsDocUrl = uploadResponse.data.url || uploadResponse.data;
          console.log('Uploaded file URL:', newsDocUrl);
        }

        const updatedFormData = { ...formData, newsUrl: newsDocUrl };

        console.log('Form data to be submitted:', updatedFormData);

        const response = await axiosInstance.post('/api/public/news/add', updatedFormData);

        console.log('News Publishing successfully:', response.data);
        setAlert({ visible: true, message: 'News published successfully!', type: 'success' });

        setFormData({
          title: '',
          newscontent: '',
          datepublished: '',
          newsUrl: ''
        });
        setSelectedFile(null);

        setTimeout(() => {
          history.goBack();
        }, 2500);
      } catch (error) {
        setAlert({ visible: true, message: 'Error publishing news. Please try again.', type: 'error' });
        console.error('Error submitting form:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setFileName(file ? file.name : '');
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value,
    });
  };

  useEffect(() => {
    setFormData({
      ...formData,
      createddate: getToday(),
      datepublished: formData.datepublished || getToday(),
      modifieddate: getToday(),
    });
  }, []);

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Create News Publishing</h1>
              </div>

              <form className="space-y-6">
                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Type className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="title"
                      maxLength={100}
                      type="text"
                      value={formData.title}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.title ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter news title"
                    />
                  </div>
                  {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    News Content <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      id="newscontent"
                      maxLength={500}
                      rows="6"
                      value={formData.newscontent}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.newscontent ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter news content"
                    ></textarea>
                  </div>
                  {errors.newscontent && <p className="text-red-500 text-xs mt-1">{errors.newscontent}</p>}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Photos
                  </label>
                  <div className="mt-1 flex items-center">
                    <label className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                      <Upload className="h-5 w-5 text-gray-400 mr-2" />
                      <span>{fileName || 'Choose file'}</span>
                      <input
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>

                <div className="w-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Published <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="datepublished"
                      type="date"
                      value={formData.datepublished}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${errors.datepublished ? "border-red-500" : "border-gray-300"} pl-10 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    />
                  </div>
                  {errors.datepublished && <p className="text-red-500 text-xs mt-1">{errors.datepublished}</p>}
                </div>

                <div className="flex justify-between items-center md:flex-row flex-col pt-4 border-t my-6">
                  <div className="text-sm text-red-500">* indicates required field</div>
                  <div className="flex gap-2 justify-between md:justify-end w-full md:w-fit  mt-2 md:mt-0">
                    <button
                      type="button"
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                      onClick={handleSubmit}
                    >
                      Publish News
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateNew;